import React, { useEffect, useState } from "react";
import Logo from "./img/Logo.png";
import { useLocation, Link } from "react-router-dom";
import BaseButton from "../Buttons/BaseButton";
import User from "./User";
import UserWithStats from "./UserWithStats";
import { sidebarMenu } from "../../services/SidebarService";
import DefaultProgressBar from "../ProgressBar/DefaultProgressBar";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { IoChatbubbles } from "react-icons/io5";
import { MdLogout, MdEmail } from "react-icons/md";
import {
  fetchChatbots,
  fetchChatbotsTotalSizeInMb,
} from "../../services/api/ManagerService";
import { fetchQueryUsage } from "../../services/api/UsersService";
import {
  useChatbotCountState,
  useChatbotSizeState,
  useQueryCountState,
} from "../../states/useStates";
import { useRecoilValue } from "recoil";
import { currentUserState, companyConstraintsState } from "../../states/atoms";
import { useWindowSize } from "@uidotdev/usehooks";
import { useNavigate } from "react-router-dom";

function Sidebar({ setOpenClose, isOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [filteredSidebarMenu, setFilteredSidebarMenu] = useState(sidebarMenu);

  const [totalSize, setTotalSize] = useChatbotSizeState();
  const [totalSizePercentage, setTotatSizePercentage] = useState(0);

  const [chatbotCount, setChatbotCount] = useChatbotCountState();
  const [queryCount, setQueryCount] = useQueryCountState();

  const user = useRecoilValue(currentUserState);
  const companyConstraints = useRecoilValue(companyConstraintsState);

  const [isExpandible, setIsExpandible] = useState(true);
  const [isTall, setIsTall] = useState(true);
  const size = useWindowSize();

  useEffect(() => {
    if (size.width && size.width < 1100) {
      setIsExpandible(false);
      setOpenClose(false);
    } else {
      setIsExpandible(true);
    }
    setIsTall(size.height >= 650);
  }, [size, setOpenClose]);

  useEffect(() => {
    // Filter menu based on user role
    if (!user.is_admin) {
      if (user.is_builder) {
        setFilteredSidebarMenu(
          sidebarMenu.filter((item) => item.title !== "Admin Area")
        );
      } else {
        setFilteredSidebarMenu([
          {
            title: "Chat",
            icon: <IoChatbubbles className="text-2xl" />,
            to: "/chat",
          },
        ]);
      }
    }

    // Fetch data counts
    fetchChatbots()
      .then((res) => setChatbotCount(res.length))
      .catch(console.error);

    fetchChatbotsTotalSizeInMb()
      .then((res) => {
        setTotalSize(res.total_size_mb);
        setTotatSizePercentage(
          (res.total_size_mb * 100) / companyConstraints.max_datasource_size_mb
        );
      })
      .catch(console.error);

    fetchQueryUsage()
      .then((res) => setQueryCount(res.query_usage))
      .catch(console.error);
  }, [user, companyConstraints, setChatbotCount, setTotalSize, setQueryCount]);

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("localAuthorization");
    localStorage.removeItem("fullname");
    navigate("/");
    window.location.reload(true);
  };

  return (
    <aside
      className={`h-screen shadow-lg relative bg-gradient-to-b from-white/30 via-white/10 to-white/5 backdrop-blur-md border-r border-white/20
        ${
          isOpen ? "w-72" : "w-20"
        } transition-all duration-300 ease-in-out flex flex-col`}
    >
      {isExpandible && (
        <button
          onClick={() => setOpenClose(!isOpen)}
          aria-label="Toggle Sidebar"
          className="absolute top-5 -right-5 bg-white/80 backdrop-blur rounded-full p-2 shadow-lg hover:bg-white transition-colors"
        >
          <MdKeyboardDoubleArrowLeft
            className={`text-primary text-xl transition-transform duration-300 ${
              isOpen ? "" : "rotate-180"
            }`}
          />
        </button>
      )}

      <div className="flex flex-col items-center py-8 gap-6">
        <Link to="/">
          <img
            src={Logo}
            alt="chatto logo"
            className={`transition-all duration-300 ${
              isOpen ? "h-24" : "h-12"
            }`}
          />
        </Link>
        <span className="font-semibold text-sm text-primary uppercase tracking-wide select-none">
          {process.env.REACT_APP_ENVIRONMENT === "sandbox"
            ? process.env.REACT_APP_ENVIRONMENT
            : ""}
        </span>
      </div>

      {isOpen ? (
        <nav className="flex-1 px-6 text-primary flex flex-col">
          {(user.is_admin || user.is_builder) && (
            <Link to="/builder/chatbots/new" className="mb-6 block">
              <BaseButton
                value="+ Create new Chatbot"
                styleClass="bg-primary text-white w-full rounded-lg shadow-md hover:shadow-lg transition"
              />
            </Link>
          )}

          <ul className="space-y-3 flex-1">
            {filteredSidebarMenu.map(({ title, icon, to }, i) => (
              <li
                key={i}
                className={`cursor-pointer rounded-lg px-3 py-2 transition-colors
            ${
              location.pathname === to
                ? "bg-primary text-white font-semibold shadow-md"
                : "hover:bg-primary/20"
            }`}
              >
                <Link to={to} className="flex items-center gap-4 text-sm">
                  {icon}
                  {title}
                </Link>
              </li>
            ))}
          </ul>

          {isTall && (
            <section className="mt-4 mb-6 border border-primary/40 border-dashed rounded-lg p-4 bg-white/10 backdrop-blur-sm shadow-inner text-primary">
              {(user.is_builder || user.is_admin) && (
                <>
                  <h5 className="text-xs font-semibold mb-2 tracking-wide">
                    USAGE SIZE
                  </h5>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span>{Math.round(totalSize)} MB</span>
                    <span>
                      {Math.round(companyConstraints.max_datasource_size_mb)} MB
                    </span>
                  </div>
                  <DefaultProgressBar
                    styleClass={
                      totalSizePercentage >= 100 ? "bg-red-500" : "bg-primary"
                    }
                    percentage={totalSizePercentage}
                  />
                  <div className="flex justify-between mt-4 text-xs font-semibold">
                    <span>CHATBOTS:</span>
                    <span>
                      {chatbotCount} / {companyConstraints.max_chatbots}
                    </span>
                  </div>
                </>
              )}

              <div className="flex justify-between mt-4 text-xs font-semibold">
                <span>DAILY QUERIES:</span>
                <span>{companyConstraints.max_daily_query - queryCount}</span>
              </div>
            </section>
          )}

          <div className="mt-auto space-y-4 mb-10">
            {/* Contact Us */}
            <button
              onClick={() =>
                (window.location.href = "mailto:support@tuodominio.com")
              }
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-primary/20 transition-colors text-primary text-sm font-medium"
              aria-label="Contact Support"
            >
              <MdEmail className="text-lg" />
              Contact Us
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-600/80 hover:text-white transition-colors text-red-600 text-sm font-medium"
              aria-label="Logout"
            >
              <MdLogout className="text-lg" />
              Logout
            </button>
            {user && (
              <User
                fullname={user.fullname}
                company={localStorage.getItem("company_name")}
              />
            )}
          </div>
        </nav>
      ) : (
        <nav className="flex flex-col items-center gap-6 px-1 mt-6">
          {(user.is_admin || user.is_builder) && (
            <Link to="/builder/chatbots/new" className="mb-12">
              <button
                className="w-12 h-12 rounded-full border border-primary text-primary text-3xl flex items-center justify-center
                  hover:bg-primary hover:text-white transition-shadow shadow-sm"
                aria-label="Create new chatbot"
              >
                +
              </button>
            </Link>
          )}
          <ul className="flex flex-col items-center w-full space-y-6">
            {filteredSidebarMenu.map(({ to, icon }, i) => (
              <li
                key={i}
                className={`cursor-pointer rounded-lg p-2 transition-colors
                  ${
                    location.pathname === to
                      ? "bg-primary text-white shadow-lg"
                      : "hover:bg-primary/20 text-primary"
                  }`}
              >
                <Link to={to} aria-label={filteredSidebarMenu[i].title}>
                  {icon}
                </Link>
              </li>
            ))}
          </ul>

          {/* <div className="mt-auto mb-6 w-full flex justify-center">
            {user && (
              <UserWithStats
                fullname={user.fullname}
                minimize={true}
                totalSize={totalSize}
                companyConstraints={companyConstraints}
                totalSizePercentage={totalSizePercentage}
                chatbotCount={chatbotCount}
                queryCount={queryCount}
              />
            )}
          </div> */}
          <div
            className="self-center bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm shadow-md cursor-default"
            title="Daily Queries Remaining"
          >
            {Math.max(companyConstraints.max_daily_query - queryCount, 0)}
          </div>

          <button
            onClick={() => {
              // qui puoi aprire mailto o altro
              window.location.href = "mailto:support@tuodominio.com";
            }}
            className="flex items-center justify-center text-primary border border-none rounded-lg hover:bg-primary/20 transition w-10 h-10"
          >
            <MdEmail className="w-6 h-6" />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center text-red-600 border border-none rounded-lg hover:bg-red-600/60 hover:text-white transition w-10 h-10"
          >
            <MdLogout className="w6 h-6" />
          </button>
        </nav>
      )}
    </aside>
  );
}

export default Sidebar;
